import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CountryStateService, State } from '@vendasta/country-state';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { PricingDynamicInput } from './pricing-details-breakdown.component';

export interface PricingDetailsDialogData {
  productName: string;
  pricing: ProductPricing[];
  onSubmit: () => void;
  showEnableButton: boolean;
}

export interface ProductPricing {
  label: string; // e.g. "Renewal Cost"
  note?: string;
  pricingDetail: PricingDetail[];
}

export interface PricingDetail {
  label: string; // e.g. "State Fees"
  note?: string;
  pricingFunc: (factors: any) => number;
}

interface StateGroup {
  letter: string;
  states: State[];
}

@Component({
  templateUrl: './pricing-details-dialog.component.html',
  styleUrls: ['./pricing-details-dialog.component.scss'],
})
export class PricingDetailsDialogComponent implements OnInit {
  stateGroupOptions$: Observable<StateGroup[]>;
  stateSearchTerm$$: BehaviorSubject<string> = new BehaviorSubject('');
  pricingDetailsForm: UntypedFormGroup;
  selectedState$: Observable<PricingDynamicInput>;

  constructor(
    public dialogRef: MatDialogRef<PricingDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public pricingDetailsDialogData: PricingDetailsDialogData,
    private countryStateService: CountryStateService,
  ) {}

  ngOnInit(): void {
    this.stateGroupOptions$ = combineLatest([
      this.stateSearchTerm$$.asObservable().pipe(startWith<string | State>('')),
      this.countryStateService.getStatesOptions('US'),
    ]).pipe(
      // group the states with the first letter
      map((states) =>
        states.reduce((groups: StateGroup[], state: State) => {
          const letter = state.name.charAt(0).toUpperCase();
          const existing = groups.find((g) => g.letter === letter);
          if (existing) {
            existing.states.push(state);
          } else {
            groups.push({ letter: letter, states: [state] });
          }
          return groups;
        }, []),
      ),
    );

    this.pricingDetailsForm = new UntypedFormGroup({
      state: new UntypedFormControl('', []),
    });

    this.selectedState$ = this.pricingDetailsForm.controls.state.valueChanges.pipe(startWith({}));
  }

  submit(): void {
    this.pricingDetailsDialogData.onSubmit();
    this.close();
  }

  close(): void {
    this.dialogRef.close();
  }

  nameDisplayFn(objectWithAName: { name: string }): string {
    return objectWithAName ? objectWithAName.name : undefined;
  }

  updateSearchTerm(term: string): void {
    this.stateSearchTerm$$.next(term);
  }
}
